import { CurrencyPipe } from '@angular/common';
import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import addDays from 'date-fns/add_days';
import format from 'date-fns/format';
import {
  divide as _divide,
  escape as _escape,
  filter as _filter,
  get as _get,
  round as _round,
  uniq as _uniq
} from 'lodash';
import { combineLatest, Subject, Subscription } from 'rxjs';
import { filter, first, take, takeUntil, tap, withLatestFrom } from 'rxjs/operators';

import { env } from '@amfam/environment';
import { PolicySelectors } from '@amfam/policy/data-access';
import { PolicyUtilService } from '@amfam/policy/e-signature/data-access';
import {
  ANSRequestModel,
  AutoPolicy,
  CustomerEmailRequest,
  MessageModel,
  MessageRequest,
  NotificationRequest,
  NotificationRequestCustomerEmail,
  Policy,
  ReplaceVehicleRequestCustomerEmail,
  RiskModel,
  SubmitOnlineBindRequestModel
} from '@amfam/policy/models';
import { PremiumDetails } from '@amfam/policy/shared';
import { vehicleQuery, VehicleSelectCriteria } from '@amfam/policy/vehicle/data-access';
import { AnalyticsFacade, EventAnalyticParams } from '@amfam/shared/analytics';
import { ISection, PaymentScheduleItem } from '@amfam/shared/models';
import { userQuery } from '@amfam/shared/user';
import { Agent, agentQuery, fromAgentActions, initialAgent } from '@amfam/shared/utility/agent';
import { BrandSelectors } from '@amfam/shared/utility/brand';
import { FeatureFlagService } from '@amfam/shared/utility/feature-flag/data-access';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import { CopyService, UtilService } from '@amfam/shared/utility/shared-services';
import { DockingBarService, ErrorLevels, ErrorObj, LoadingSpinnerService } from '@amfam/ui-kit';

import {
  AnalyticsEventType,
  ReplaceVehicleQuickQuoteContext,
  ReplaceVehicleQuickQuoteOperation
} from '../+state/quick-quote-feature/quick-quote-enum';
import { QuickQuoteFeatureFacade } from '../+state/quick-quote-feature/quick-quote-feature.facade';
import { ReviewQuoteContainerService } from '../review-quote-container/review-quote-container.service';
import { QuickQuoteFeatureService } from '../services/quick-quote-feature.service';

@Component({
  selector: 'quick-quote-comparison',
  templateUrl: './quick-quote-comparison.component.html',
  styleUrls: ['./quick-quote-comparison.component.scss'],
  standalone: false
})
export class QuickQuoteComparisonComponent implements OnInit, OnDestroy, AfterViewInit {
  policyNumberFromVehicle$ = this.quickQuoteFeatureFacade.policyNumberFromVehicle$;
  errorOnANS$ = this.quickQuoteFeatureFacade.errorOnANS$;
  newRiskDescription$ = this.quickQuoteFeatureFacade.newRiskDescription$;
  existingVehicleRiskDescription$ = this.quickQuoteFeatureFacade.existingVehicleRiskDescription$;
  context$ = this.quickQuoteFeatureFacade.context$;
  private stop$ = new Subject<void>();

  public premiumDetails: PremiumDetails;
  private subscription: Subscription = new Subscription();
  errors: Array<ErrorObj> = [];
  private vehicleId: string;

  private policyList: AutoPolicy[];
  private matchingvehicle: RiskModel;
  public currentVehicle = 'current vehicle';
  public newVehicle: string;
  public newVehicleVin: string;
  private isUsedForBusiness: NotificationRequest['isUsedForBusiness'];
  private isOwner: NotificationRequest['isOwner'];
  private hasTPI: NotificationRequest['hasTPI'];
  private existingTPI: NotificationRequest['existingTPI'];
  private copyTPIIndicator: NotificationRequest['copyTPIIndicator'];
  private selectedPolicy: Policy;
  private selectedProducer: Agent;
  private partnerId: string;
  private customerId: string;
  private customerEmail: string;
  private customerName: string;
  private customerFirstName: string;
  private customerLastName: string;
  private customerPhone: string;
  private stateCode: string;
  private criteria: VehicleSelectCriteria;
  public errorList: Array<ErrorObj> = [];
  private msrp: string;
  private estimatedVehiclePrice: string;

  constructor(
    protected quickQuoteFeatureFacade: QuickQuoteFeatureFacade,
    protected store: Store,
    protected spinnerService: LoadingSpinnerService,
    protected router: Router,
    protected copyService: CopyService,
    protected dockingBarService: DockingBarService,
    protected utilService: UtilService,
    protected route: ActivatedRoute,
    protected policyUtilService: PolicyUtilService,
    protected quickQuoteFeatureService: QuickQuoteFeatureService,
    protected reviewQuoteContainerService: ReviewQuoteContainerService,
    protected featureFlagService: FeatureFlagService,
    protected analyticsFacade: AnalyticsFacade,
    protected currencyPipe: CurrencyPipe
  ) {}

  ngAfterViewInit(): void {
    this.context$.pipe(take(1)).subscribe(context => {
      if (context === ReplaceVehicleQuickQuoteContext.QUICK_QUOTE) {
        this.quickQuoteFeatureFacade.sendAnalytics(
          AnalyticsEventType.QUICK_QUOTE_COMPLETE,
          '',
          this.premiumDetails?.orderNumber || ''
        );
        this.dockingBarService.registerHeading('QUICK QUOTE');
      }
    });
  }

  ngOnInit() {
    this.setError();
    this.handleSpinnerOnError();
    this.quickQuoteFeatureFacade.replaceVehicleId$
      .pipe(take(1))
      .subscribe(vehicleId => (this.vehicleId = vehicleId));

    this.showQuickQuote();
    this.showReplaceVehicleQuote();

    // GA analytics for Quick quote Complete
    const quickQuoteCompleteEvent: EventAnalyticParams = {
      event: 'quick_quote_complete',
      product_line: 'Auto',
      form_name: 'Quick Quote',
      quote_id: this.premiumDetails.orderNumber
    };
    this.analyticsFacade.trackEvent(quickQuoteCompleteEvent);
  }

  showQuickQuote() {
    this.subscription.add(
      combineLatest([
        this.store.select(PolicySelectors.selectAdvanceAutoPolicies),
        this.quickQuoteFeatureFacade.quickQuote$
      ]).subscribe(([policy, premium]) => {
        const matchingPolicy = policy[0];
        const currentTermMonths = Number(matchingPolicy.termDescription.split(' ')[0]);
        const monthlyCost = _round(
          _divide(matchingPolicy?.totalPremium, currentTermMonths),
          2
        ).toFixed(2);
        this.premiumDetails = {
          current: {
            description: 'Current',
            fullCost: Number(matchingPolicy?.totalPremium || 0).toFixed(2),
            policyTermMonths: currentTermMonths + ' mos.',
            monthlyCost
          },
          new: {
            description: 'Quote',
            fullCost: Number(premium?.totalPremium || 0).toFixed(2),
            policyTermMonths: premium?.termMonths + ' mos.',
            monthlyCost: Number(premium?.monthlyPremium || 0).toFixed(2)
          },
          orderNumber: premium?.workOrderNumber,
          quoteExpirationDate: new Date()
        };
      })
    );
  }

  showReplaceVehicleQuote() {
    combineLatest(
      this.route.params,
      this.store.select(PolicySelectors.selectAdvanceAutoPolicies),
      this.store.select(PolicySelectors.selectAdvanceAutoPolicyRisks),
      this.store.select(vehicleQuery.selectNewVehicle),
      this.store.select(vehicleQuery.selectCriteria),
      this.store.select(agentQuery.getAgentEntities),
      this.store.select(userQuery.selectUserStateCode),
      (params, policies, risks, newVehicle, vehicleSelectionDetails, agents, userState) => {
        return {
          vehicleId: params['id'],
          policies: policies,
          vehicles: risks,
          newVehicle: newVehicle,
          vehicleSelectionDetails: vehicleSelectionDetails,
          agents: agents,
          userState: userState
        };
      }
    )
      .pipe(takeUntil(this.stop$))
      .subscribe(state => {
        this.vehicleId = state.vehicleId;
        this.policyList = state.policies;
        this.stateCode = state.userState;
        this.criteria = state.vehicleSelectionDetails;

        let matchingPolicy;

        const matchingVehicle = state.vehicles.find(vehicle => {
          return _get(vehicle, 'id') === this.vehicleId;
        });

        if (matchingVehicle) {
          const policyNumber = matchingVehicle.policyNumber;
          this.matchingvehicle = matchingVehicle;

          matchingPolicy = state.policies.find(policy => {
            return _get(policy, 'policyNumber') === policyNumber;
          });

          if (state.newVehicle) {
            this.analyticsFacade.trackEvent({
              event: 'replace_vehicle_quote_summary',
              form_name: 'Replace Vehicle Flow',
              vehicle_make: _get(state, 'newVehicle.make', ''),
              vehicle_model: _get(state, 'newVehicle.model', ''),
              vehicle_year: _get(state, 'newVehicle.year', ''),
              quote_id: this.premiumDetails.orderNumber,
              product_line: 'auto'
            });
          }

          if (matchingPolicy) {
            const currentTermMonths = Number(matchingPolicy.termDescription.split(' ')[0]);
            const monthlyCost = _round(_divide(matchingPolicy.totalPremium, currentTermMonths), 2);
            this.newVehicle =
              _get(state, 'newVehicle.year') +
              ' ' +
              _get(state, 'newVehicle.make', '') +
              ' ' +
              _get(state, 'newVehicle.model', '');
            this.newVehicleVin = _get(state, 'newVehicle.vin', '');
            this.premiumDetails.current.description = 'Current';
            this.premiumDetails.current.fullCost = Number(
              _get(matchingPolicy, 'totalPremium', 0)
            ).toFixed(2);
            this.premiumDetails.current.policyTermMonths = currentTermMonths + ' mos.';
            this.premiumDetails.current.monthlyCost = monthlyCost.toFixed(2);

            this.premiumDetails.new.description = 'Quote';
            this.premiumDetails.new.newVehicle = this.newVehicle;
            this.msrp = state?.newVehicle?.msrp || 'N/A';
            this.estimatedVehiclePrice = state?.newVehicle?.estimatedValue || 'N/A';

            if (
              this.matchingvehicle &&
              this.matchingvehicle.year &&
              this.matchingvehicle.make &&
              this.matchingvehicle.model
            ) {
              this.currentVehicle =
                this.matchingvehicle.year +
                ' ' +
                this.matchingvehicle.make +
                ' ' +
                this.matchingvehicle.model;
            }

            this.isUsedForBusiness = this.policyUtilService.getUnderWritingQuestionsDetails(
              _get(state, 'vehicleSelectionDetails'),
              'policy.newVehicleUnderWritingQuestionIsBusinessUse',
              true
            );

            this.isOwner = this.policyUtilService.getUnderWritingQuestionsDetails(
              _get(state, 'vehicleSelectionDetails'),
              'policy.newVehicleUnderWritingQuestionIsOwner',
              true
            );

            this.hasTPI = this.policyUtilService.getUnderWritingQuestionsDetails(
              _get(state, 'vehicleSelectionDetails'),
              'policy.newVehicleUnderWritingQuestionHasLoan',
              true
            );

            this.existingTPI = _get(state, 'vehicleSelectionDetails.TPIName', '');

            this.copyTPIIndicator =
              _get(state, 'vehicleSelectionDetails.copyTPIIndicator', '') === 'yes' ? 'Yes' : 'No';

            this.selectedPolicy = matchingPolicy;
            // JP: Check if we've loaded producer already - dispatch load if not
            this.selectedProducer =
              state.agents[_get(this.selectedPolicy, 'assignedProducer.producerIdNum')];
            if (!this.selectedProducer) {
              this.store.dispatch(
                new fromAgentActions.LoadAgentAction(
                  Object.assign({}, initialAgent, {
                    id: _get(this.selectedPolicy, 'assignedProducer.producerIdNum')
                  })
                )
              );
            }
          }
        }
      });

    // JP: After Load action has been dispatched, subscribe again and fetch the correct producer
    if (!this.selectedProducer) {
      combineLatest(
        this.store.select(agentQuery.getAreAllAgentsLoaded),
        this.store.select(agentQuery.getAgentEntities),
        (loaded, agents) => {
          return {
            loaded: loaded,
            agents: agents
          };
        }
      )
        .pipe(
          filter(data => data.loaded),
          takeUntil(this.stop$)
        )
        .subscribe(data => {
          this.selectedProducer =
            data.agents[_get(this.selectedPolicy, 'assignedProducer.producerIdNum')];
        });
    }

    combineLatest(
      this.store.select(userQuery.selectCustomerId),
      this.store.select(userQuery.selectEmailAddress),
      this.store.select(userQuery.selectPrimaryPhone),
      this.store.select(userQuery.selectFirstName),
      this.store.select(userQuery.selectLastName),
      this.store.select(BrandSelectors.selectPartnerId),
      this.store.select(BrandSelectors.selectIsPartner),
      (
        customerId,
        userEmailAddress,
        userPrimaryPhone,
        userFirstName,
        userLastName,
        partner,
        isPartner
      ) => ({
        customerId,
        userEmailAddress,
        userPrimaryPhone,
        userFirstName,
        userLastName,
        partner,
        isPartner
      })
    ).subscribe(res => {
      this.partnerId = res.partner;
      this.customerFirstName = _get(res, 'userFirstName', '');
      this.customerLastName = _get(res, 'userLastName', '');
      this.customerName = this.customerFirstName + ' ' + this.customerLastName;
      this.customerId = _get(res, 'user.customerId', '');
      this.customerEmail = _get(res, 'userEmailAddress', '');
      this.customerPhone = _get(
        res,
        'userPrimaryPhone.tenDigitPhoneNumber',
        'No phone number on record'
      );
    });
  }

  sendToAgent() {
    this.spinnerService.start({ blockActions: true });
    this.quickQuoteFeatureFacade.sendToAgent();
  }

  goToReplaceVehicleScreen() {
    this.quickQuoteFeatureFacade.resetQuickQuote();
    this.quickQuoteFeatureFacade.sendAnalytics(
      AnalyticsEventType.BUTTON_COMPLETE_GET_ANOTHER_QUOTE
    );
    this.vehicleId
      ? this.store.dispatch(
          fromRouterActions.Go({
            path: [`/policies/risk-selection/replace-vehicle-quick-quote/${this.vehicleId}`]
          })
        )
      : this.store.dispatch(fromRouterActions.Back());
  }

  backToVehicleDetails() {
    this.policyNumberFromVehicle$
      .pipe(
        filter(policyNumber => !!policyNumber),
        first(),
        tap(policyNumber => this.router.navigateByUrl(`/policies/auto/${policyNumber}`))
      )
      .subscribe();
  }

  setError() {
    this.errors = [];
    this.errors.push(<ErrorObj>{
      errorLevel: ErrorLevels.HIGH,
      errorMessage: this.copyService.getCopy('policy.quickQuoteErrorMessage')
    });
  }

  handleSpinnerOnError() {
    this.errorOnANS$
      .pipe(
        filter(error => !!error),
        takeUntil(this.stop$),
        tap(() => this.spinnerService.stop())
      )
      .subscribe();
  }

  handlePrimaryEventTrigger() {
    this.context$
      .pipe(
        withLatestFrom(
          this.quickQuoteFeatureFacade.replaceVehicleId$,
          this.quickQuoteFeatureFacade.futurePayments$,
          this.reviewQuoteContainerService.policyCoverages$,
          this.reviewQuoteContainerService.vehicleCoverages$,
          this.reviewQuoteContainerService.discounts$
        ),
        take(1)
      )
      .subscribe(
        ([context, vehicleId, futurePayments, policyCoverages, vehicleCoverages, discounts]) => {
          if (context === ReplaceVehicleQuickQuoteContext.REPLACE_VEHICLE) {
            this.quickQuoteFeatureFacade.setQuoteToBind(
              this.getQuoteToBind(futurePayments, policyCoverages, vehicleCoverages, discounts)
            );
            if (!futurePayments || futurePayments.length === 0) {
              this.spinnerService.start({ blockActions: true });
              this.quickQuoteFeatureFacade.setOperation(
                ReplaceVehicleQuickQuoteOperation.REPLACE_VEHICLE_BIND
              );
              this.quickQuoteFeatureFacade.bindQuoteOnline();
            } else {
              this.store.dispatch(
                fromRouterActions.Go({
                  path: [
                    `/policies/risk-selection/replace-vehicle-quick-quote/${vehicleId}/payment-schedule`
                  ]
                })
              );
            }
          } else {
            this.sendToAgent();
          }
        }
      );
  }

  handleSecondaryEventTrigger() {
    this.context$.pipe(take(1)).subscribe(context => {
      if (context === ReplaceVehicleQuickQuoteContext.REPLACE_VEHICLE) {
        this.goToReplaceVehicleScreen();
      } else {
        this.backToVehicleDetails();
      }
    });
  }

  handleTertiaryEventTrigger() {
    this.context$.pipe(take(1)).subscribe(context => {
      if (context === ReplaceVehicleQuickQuoteContext.REPLACE_VEHICLE) {
        this.quickQuoteFeatureFacade.setOperation(
          ReplaceVehicleQuickQuoteOperation.REPLACE_VEHICLE_SEND_AGENT
        );
        this.submit(context);
      } else {
        this.goToReplaceVehicleScreen();
      }
    });
  }

  /**
   * AS: This function will be called when the customer clicks on sent to agent button.
   */
  submit(context: ReplaceVehicleQuickQuoteContext) {
    const customerNotificationRequest: NotificationRequestCustomerEmail = {
      email: new ReplaceVehicleRequestCustomerEmail(this.getCustomerEmailRequestModel()),
      message: new MessageModel(this.getMessageModel()),
      templateId: 'ReplaceVehicleQuoteSubmitted'
    };

    customerNotificationRequest.message.policyNumber = this.utilService.maskPolicyNumber(
      _get(this.selectedPolicy, 'policyNumber', '')
    );

    const customerName = this.customerName;
    const policyNumber = _get(this.selectedPolicy, 'policyNumber', '');
    const newVehicle =
      context === ReplaceVehicleQuickQuoteContext.REPLACE_VEHICLE
        ? `${customerNotificationRequest.message.newRiskInformation}, VIN ${this.criteria.vin}`
        : customerNotificationRequest.message.newRiskInformation;
    const oldVehicle =
      context === ReplaceVehicleQuickQuoteContext.REPLACE_VEHICLE
        ? `${customerNotificationRequest.message.riskInformation}, VIN ${this.matchingvehicle.vin}`
        : customerNotificationRequest.message.riskInformation;
    const orderNumber = _get(this.premiumDetails, 'orderNumber');
    const existVehicleVin = customerNotificationRequest.message.existingVehicleVin;
    const newVehicleVin = customerNotificationRequest.message.newVehicleVin;
    const notificationMsg = this.copyService.getCopy('policy.ANSContentQuoteToAgent', {
      customerName: customerName,
      policyNumber: policyNumber,
      newVehicle: newVehicle,
      oldVehicle: oldVehicle,
      newVehicleVin: newVehicleVin,
      existVehicleVin: existVehicleVin,
      orderNumber: orderNumber,
      newVehicleMsrp: this.formatMSRP(this.msrp) || 'N/A',
      estimatedVehiclePrice: this.formatMSRP(this.estimatedVehiclePrice) || 'N/A',
      hasTPI: this.hasTPI,
      existingTPI:
        this.hasTPI === 'Yes'
          ? this.existingTPI
            ? this.existingTPI
            : 'Not Provided'
          : 'Not Applicable'
    });

    const ansRequest: ANSRequestModel = new ANSRequestModel({
      producer: this.selectedProducer,
      orderNumber: _get(this.premiumDetails, 'orderNumber'),
      policyNumber: _get(this.selectedPolicy, 'policyNumber'),
      from: this.copyService.getCopy('shared.myAccountTxt'),
      customerName: this.customerName,
      notificationText: notificationMsg,
      vehicleId: this.vehicleId
    });

    let agentNotificationRequest: NotificationRequest = {
      producer: this.selectedProducer,
      vehicleId: this.vehicleId,
      customerName: customerName,
      policyNumber: policyNumber,
      orderNumber: _get(this.premiumDetails, 'orderNumber'),
      newVehicle: newVehicle,
      oldVehicle: oldVehicle,
      existingVehicleVin: existVehicleVin,
      newVehicleVin: newVehicleVin,
      email: this.customerEmail,
      type: 'Quote to agent',
      copyTPIIndicator: this.copyTPIIndicator,
      isUsedForBusiness: this.isUsedForBusiness,
      isOwner: this.isOwner,
      msrp: this.msrp,
      estimatedVehiclePrice: this.estimatedVehiclePrice,
      hasTPI: this.hasTPI,
      existingTPI: this.existingTPI
    };

    this.quickQuoteFeatureService
      .buildANSNotificationPayload(
        this.selectedProducer.agentId,
        this.selectedPolicy.policyNumber,
        ansRequest.notificationText,
        'NA'
      )
      .pipe(take(1))
      .subscribe(payload => {
        this.quickQuoteFeatureFacade.replaceVehicleANSNotification(payload);
      });

    this.quickQuoteFeatureFacade.replaceVehicleNotifyAgent(
      this.quickQuoteFeatureService.buildInternalEmail(agentNotificationRequest)
    );

    this.quickQuoteFeatureFacade.replaceVehicleNotifyCustomer(
      this.quickQuoteFeatureService.buildCustomerEmail(
        customerNotificationRequest.email,
        this.quickQuoteFeatureService.buildMessageDetails(customerNotificationRequest.message),
        customerNotificationRequest.templateId
      )
    );
    this.quickQuoteFeatureFacade.sendAnalytics(AnalyticsEventType.REPLACE_VEHICLE_SEND_TO_AGENT);
  }

  /**
   * AS: This function will be called when the customer clicks on sent to bind now button.
   */
  getQuoteToBind(
    futurePayments: PaymentScheduleItem[],
    policyCoverages: ISection,
    vehicleCoverages: ISection,
    discounts: ISection
  ): SubmitOnlineBindRequestModel {
    const customerNotificationRequest: NotificationRequestCustomerEmail = {
      email: new ReplaceVehicleRequestCustomerEmail(this.getCustomerEmailRequestModel()),
      message: new MessageModel(this.getMessageModel()),
      templateId: this.featureFlagService.isEnabled('replace_vehicle_plpc_migration') // TODO: check this
        ? 'MYACCT_RPL_VEH_SUCCESS'
        : 'ReplaceVehicleBound'
    };

    customerNotificationRequest.message.policyNumber = this.utilService.maskPolicyNumber(
      _get(this.selectedPolicy, 'policyNumber', '')
    );

    const customerName = this.customerName;
    const policyNumber = _get(this.selectedPolicy, 'policyNumber', '');
    const newVehicle = customerNotificationRequest.message.newRiskInformation;
    const oldVehicle = customerNotificationRequest.message.riskInformation;
    const existingVehicleVin = customerNotificationRequest.message.existingVehicleVin;
    const newVehicleVin = customerNotificationRequest.message.newVehicleVin;
    const orderNumber = _get(this.premiumDetails, 'orderNumber');

    const hasTPIChanged = this.hasTPI === 'Yes' && this.copyTPIIndicator === 'No';

    const copyPath = hasTPIChanged
      ? 'policy.ANSContentQuoteTPIChangeNeeded'
      : 'policy.ANSContentQuoteNoTPIChangeNeeded';

    const emailSubjectNoActionNeeded = this.copyService.getCopy(
      'policy.emailSubjectOnlineBindNoAction'
    );
    const emailSubjectActionNeeded = this.copyService.getCopy(
      'policy.emailSubjectOnlineBindActionNeeded'
    );
    const vehicleCount = this.matchingvehicle?.policy?.policyRisks?.length || 0;

    const notificationMsg = this.copyService.getCopy(copyPath, {
      customerName: customerName,
      policyNumber: policyNumber,
      newVehicle: newVehicle,
      oldVehicle: oldVehicle,
      existingVehicleVin: existingVehicleVin,
      newVehicleVin: newVehicleVin,
      email: this.customerEmail,
      isUsedForBusiness: this.isUsedForBusiness,
      isOwner: this.isOwner,
      hasTPI: this.hasTPI,
      existingTPI:
        this.hasTPI === 'Yes'
          ? this.existingTPI
            ? this.existingTPI
            : 'Not Provided'
          : 'Not Applicable',
      orderNumber: orderNumber,
      newVehicleMsrp: this.formatMSRP(this.msrp) || 'N/A',
      estimatedVehiclePrice: this.formatMSRP(this.estimatedVehiclePrice) || 'N/A'
    });

    const agentNotificationRequest: NotificationRequest = {
      producer: this.selectedProducer,
      orderNumber: _get(this.premiumDetails, 'orderNumber'),
      vehicleId: this.vehicleId,
      customerName: customerName,
      policyNumber: policyNumber,
      newVehicle: newVehicle,
      oldVehicle: oldVehicle,
      existingVehicleVin: existingVehicleVin,
      newVehicleVin: newVehicleVin,
      email: this.customerEmail,
      copyTPIIndicator: this.copyTPIIndicator,
      type: 'Quote',
      isUsedForBusiness: this.isUsedForBusiness,
      isOwner: this.isOwner,
      hasTPI: this.hasTPI,
      existingTPI: this.existingTPI,
      msrp: this.msrp,
      estimatedVehiclePrice: this.estimatedVehiclePrice
    };

    const internalAgentEmail = _get(this.selectedProducer, 'agencyEmails[0]')
      ? env.policyGracePeriodNotificatonInternalAgencyMailbox
      : env.policyGracePeriodNotificatonInternal;

    const onlineBindReqPayload: SubmitOnlineBindRequestModel = {
      policyNumber: _get(this.selectedPolicy, 'policyNumber', ''),
      state: this.stateCode,
      notificationSubject: hasTPIChanged ? emailSubjectActionNeeded : emailSubjectNoActionNeeded,
      notificationText: notificationMsg,
      notificationType: 'ANSMYABIND',
      emailSubject: hasTPIChanged ? emailSubjectActionNeeded : emailSubjectNoActionNeeded,
      emailBody: this.featureFlagService.isEnabled('replace_vehicle_plpc_migration')
        ? this.quickQuoteFeatureService.generateInternalEmailContent(agentNotificationRequest)
        : _escape(
            this.quickQuoteFeatureService.generateInternalEmailContent(agentNotificationRequest)
          ),
      workOrderNumber: orderNumber,
      vehicleId: this.vehicleId,
      sourceSystem: 'MyAccountWebApplication',
      customer: {
        firstName: this.customerFirstName,
        lastName: this.customerLastName,
        phone: this.customerPhone,
        email: this.customerEmail,
        emailTemplateId: customerNotificationRequest.templateId,
        id: this.customerId
      },
      producer: {
        firstName: this.selectedProducer.firstName,
        lastName: this.selectedProducer.lastName,
        producerId: this.selectedProducer.id,
        phone: _get(
          this.selectedProducer,
          'phones[0].phoneNumber',
          _get(this.selectedProducer, 'phones[0].phoneNumber', 'No phone number on record')
        ),
        fromAddress: env.policyChangeRequestInternal,
        fromAddressLabel: this.copyService.getCopy('policy.policyChangeAgentEmailLabel'),
        email: env.production
          ? _get(
              this.selectedProducer,
              'agencyEmails[0]',
              _get(this.selectedProducer, 'emails[0]', 'No email address on record')
            )
          : internalAgentEmail
      },
      emailTemplateData: {
        RiskInformation: _get(this.matchingvehicle, 'description', ''),
        TotalPremium: _get(this.premiumDetails, 'current.fullCost', ''),
        MonthlyPremium: _get(this.premiumDetails, 'current.monthlyCost', ''),
        TermDescription: _get(this.premiumDetails, 'current.policyTermMonths', ''),
        NewRiskInformation: _get(this, 'newVehicle', ''),
        NewTotalPremium: _get(this.premiumDetails, 'new.fullCost', ''),
        NewMonthlyPremium: _get(this.premiumDetails, 'new.monthlyCost', ''),
        NewTermDescription: _get(this.premiumDetails, 'new.policyTermMonths', ''),
        Drivers: customerNotificationRequest?.message?.drivers,
        GaragedAddress: customerNotificationRequest?.message?.garagedAddress,
        Coverages: customerNotificationRequest?.message?.coverages,
        Fees: customerNotificationRequest?.message?.fees,
        Discounts: customerNotificationRequest?.message?.discounts,
        PolicyNumber: customerNotificationRequest.message.policyNumber,
        VehicleCount: vehicleCount
      },
      partnerId: this.partnerId,
      policyCoverages: this.reviewQuoteContainerService.transformKeyValuPairs(
        'PolicyCoverage',
        policyCoverages?.items || []
      ),
      vehicleCoverages: this.reviewQuoteContainerService.transformKeyValuPairs(
        'VehicleCoverage',
        vehicleCoverages?.items || []
      ),
      discounts: this.reviewQuoteContainerService.transformKeyValuPairs(
        'Discounts',
        discounts?.items || [],
        true
      ),
      futurePayments: this.reviewQuoteContainerService.transformKeyValuPairs(
        'FuturePaymentSchedule',
        this.reviewQuoteContainerService.mapFuturePayments(futurePayments) || []
      )
    };
    return onlineBindReqPayload;
  }

  private getCustomerEmailRequestModel(): CustomerEmailRequest {
    const customerEmailRequest: CustomerEmailRequest = {
      customerEmailId: this.customerEmail,
      customerId: this.customerId,
      partnerId: this.partnerId
    };
    return customerEmailRequest;
  }

  private getMessageModel(): MessageRequest {
    let drivers: string;
    let coverages: string;
    let discounts: string;
    let fees: string;

    for (const vehicleNamedInsured of _uniq(
      _filter(this.matchingvehicle.vehicleRoles, { roleType: 'PrimaryDriver' })
    )) {
      if (drivers) {
        drivers += ', ' + _get(vehicleNamedInsured, 'name');
      } else {
        drivers = _get(vehicleNamedInsured, 'name');
      }
    }
    for (const vehicleCoverage of _uniq(_get(this.matchingvehicle, 'vehicleCoverages', []))) {
      if (coverages) {
        coverages += ', ' + _get(vehicleCoverage, 'name');
      } else {
        coverages = _get(vehicleCoverage, 'name');
      }
    }
    // Pull vehicle-level discounts
    for (const vehicleDiscount of _uniq(_get(this.matchingvehicle, 'vehicleDiscounts', []))) {
      if (discounts) {
        discounts += ', ' + _get(vehicleDiscount, 'name');
      } else {
        discounts = _get(vehicleDiscount, 'name');
      }
    }
    // Pull policy-level discounts
    for (const policyDiscount of _uniq(_get(this.matchingvehicle, 'policy.policyDiscounts', []))) {
      if (discounts) {
        discounts += ', ' + _get(policyDiscount, 'name');
      } else {
        discounts = _get(policyDiscount, 'name');
      }
    }
    // Pull vehicle-level fees
    for (const vehicleFee of _uniq(_get(this.matchingvehicle, 'vehicleFees', []))) {
      if (fees) {
        fees += ', ' + _get(vehicleFee, 'name');
      } else {
        fees = _get(vehicleFee, 'name');
      }
    }
    // Pull policy-level fees
    for (const policyFee of _uniq(_get(this.matchingvehicle, 'policy.policyFees', []))) {
      if (fees) {
        fees += ', ' + _get(policyFee, 'name');
      } else {
        fees = _get(policyFee, 'name');
      }
    }
    for (const vehicleSurchanges of _uniq(
      _get(this.matchingvehicle, 'policy.policySurcharges', [])
    )) {
      if (fees) {
        fees += ', ' + _get(vehicleSurchanges, 'name');
      } else {
        fees = _get(vehicleSurchanges, 'name');
      }
    }

    const quoteExpDate: any = _get(this.premiumDetails, 'quoteExpirationDate');

    const messageModelRequest: MessageRequest = {
      firstName: this.customerFirstName,
      policyNumber: this.selectedPolicy.policyNumber,

      riskInformation: this.matchingvehicle.description,
      existingVehicleVin: this.matchingvehicle.vin,
      totalPremium: _get(this.premiumDetails, 'current.fullCost'),
      monthlyPremium: _get(this.premiumDetails, 'current.monthlyCost'),
      termDescription: _get(this.premiumDetails, 'current.policyTermMonths'),

      newRiskInformation: this.newVehicle,
      newVehicleVin: this.newVehicleVin,
      newTotalPremium: _get(this.premiumDetails, 'new.fullCost'),
      newMonthlyPremium: _get(this.premiumDetails, 'new.monthlyCost'),
      newTermDescription: _get(this.premiumDetails, 'new.policyTermMonths'),
      // AS: If we do not have the quote exp date coming back assume the current date and send the mail
      quoteExpirationDate: quoteExpDate
        ? format(addDays(new Date(quoteExpDate), 60), 'MM/DD/YYYY')
        : format(addDays(new Date(), 60), 'MM/DD/YYYY'),
      garagedAddress: _get(this.matchingvehicle, 'address.addressLine1'),

      drivers: drivers,
      discounts: discounts,
      fees: fees,
      coverages: coverages,
      orderNumber: _get(this.premiumDetails, 'orderNumber', ''),
      producerId: _get(this, 'selectedProducer.id', ''),
      msrp: this.msrp,
      estimatedVehicleValue: this.estimatedVehiclePrice
    };
    return messageModelRequest;
  }

  private showErrorMessage() {
    this.errorList = [];
    this.errorList.push({
      errorMessage: this.copyService.getCopy('policy.onlineBindFailureMessage'),
      errorLevel: ErrorLevels.HIGH
    });
  }

  private formatMSRP(msrp: string) {
    if (!msrp) {
      return '';
    }
    const value = msrp.replace(/[^0-9.-]+/g, '');
    return this.currencyPipe.transform(value, 'USD', 'symbol', '1.0-2');
  }

  ngOnDestroy() {
    this.stop$.next();
    this.stop$.complete();
    this.spinnerService.stop();
    this.subscription.unsubscribe();
  }
}
